import { usePinch, UserHandlers } from "@use-gesture/react";
import classNames from "classnames";
import {
  action,
  autorun,
  comparer,
  computed,
  reaction,
  runInAction,
  untracked,
} from "mobx";
import { observer } from "mobx-react-lite";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { LayerContainer, PopperLayer } from "react-atmosphere";
import { canvasSelectionAtom, uiStateMobx } from "../SharedIframeState";
import { ActionType, PaneTypeNames, Position } from "../Types";
import { AnimatedPopout } from "../ui/AnimatedPopout";
import { assert } from "../Utils";
import { CanvasBackground } from "./CanvasBackground";
import { addSelectionToClipboard } from "./CanvasClipboard";
import { canvasDispatch } from "./CanvasDispatch";
import { CanvasNameUnitSandbox } from "./CanvasNameUnitSandbox";
import {
  activePaneStates,
  canvasAtom,
  canvasLayoutIdAtom,
  canvasTransformComputed,
  canvasViewport,
  canvasViewportShouldAnimateAtom,
  focusedPaneIdAtom,
  headerHeightComputed,
  isEditingLayoutAtom,
  keyboardTransformPaneIdBox,
  magicLayoutAtom,
  sandboxLayoutOverrideBox,
  selectedPaneIdsAtom,
  urlLayoutIdAtom,
  usedDefaultLayoutAtom,
} from "./CanvasState";
import {
  CLIPBOARD_PASTE_ID,
  DragClipboardPastePreview,
  DragClipboardPasteStartEventEmitter,
} from "./ClipboardPane";
import { ParentComlinkAPI } from "./FrameSandboxAPI";
import { HeaderLayoutButton } from "./HeaderLayoutButton";
import { HeaderPresenceButton } from "./HeaderPresenceButton";
import { setupPaneCanvasKeyboard } from "./KeyboardEffects";
import { LayoutEditPane } from "./LayoutEditPane";
import { MinimapPane } from "./Minimap";
import {
  dragValueAtom,
  getFocusPaneIdOnMount,
  paneToolDragMobx,
} from "./NewPane";
import { NewPaneContextMenu } from "./NewPaneContextMenu";
import { PaneComponent, scrollPaneIntoViewport } from "./PaneComponent";
import { DragPaneOutputWireImpl } from "./PaneInput";
import { computeElkLayout } from "./PaneLayout";
import { PresenceCursors } from "./PresenceCursors";
import {
  CANVAS_PORTAL_ID,
  MAGIC_LAYOUT_ID,
  MAGIC_LAYOUT_NAME,
} from "./SandboxConstants";
import {
  addPositions,
  calculateNewZoomCanvasOffset,
  convertViewportPositionToCanvasOffset,
  hasFocusGuardOpen,
  scalePosition,
  setSelectedToolToPointer,
  TOPBAR_CLASSNAME,
} from "./SandboxUtils";
import { DragEvalPaneTemplatePreview } from "./TemplateBrowserPane";
import {
  ToolbarAddPaneButton,
  ToolbarPanButton,
  ToolbarPointerButton,
} from "./ToolbarButtons";
import { setupZoom, ToolbarZoomButton } from "./ToolbarZoomButton";
import { TutorialPane } from "./Tutorial";

const DragPaneOutputWire = observer(() => {
  const { value } = dragValueAtom;
  if (value?.type !== "reference") {
    return null;
  }
  const { fromPaneId, fromPaneOutputIndex, position } = value;
  if (fromPaneId === undefined || position === undefined) {
    return null;
  }
  const sourceInfoPaneOutput =
    value.sourceInfo.type === "pane-output" ? value.sourceInfo : undefined;
  return (
    <DragPaneOutputWireImpl
      fromPaneId={fromPaneId}
      fromPaneOutputIndex={fromPaneOutputIndex}
      duplicateInputName={sourceInfoPaneOutput?.duplicateInputName}
      toPaneId={sourceInfoPaneOutput?.hoverPaneId}
      toPaneInputId={sourceInfoPaneOutput?.hoverPaneInputKey?.[2]}
      mousePosition={position}
      key={sourceInfoPaneOutput?.hoverPaneId !== undefined ? "pane" : "mouse"}
    />
  );
});

const DragNewPanePreview = observer(() => {
  const dragNewPane = paneToolDragMobx.get();
  const { value } = dragValueAtom;
  let position: Position | undefined;
  let size: Position | undefined;
  if (dragNewPane !== undefined) {
    position = dragNewPane.position;
    size = dragNewPane.size;
  } else if (
    value !== undefined &&
    !(
      value.type === "reference" &&
      value.sourceInfo.type === "pane-output" &&
      value.sourceInfo.hoverPaneId !== undefined
    )
  ) {
    position = value.position;
  }
  if (position === undefined) {
    return null;
  }
  const isTooSmall = size !== undefined && (size[0] < 64 || size[1] < 64);
  return (
    <div
      className={classNames(
        "absolute top-0 left-0 bg-neutral-0 rounded shadow-lg pointer-events-none z-999999",
        isTooSmall ? "opacity-50" : "opacity-90"
      )}
      style={{
        transform: `translate(${position[0]}px, ${position[1]}px)`,
        width: `${(size !== undefined ? size[0] : 256) + 1}px`,
        height: `${(size !== undefined ? size[1] : 192) + 1}px`,
      }}
    >
      <div className="pt-2 pl-2 font-mono-natto text-neutral-400">
        {value?.type === "reference" && value.sourceInfo.type === "data-column"
          ? value.sourceInfo.columnName
          : value?.type === "reference" &&
            value.sourceInfo.type === "pane-output" &&
            value.sourceInfo.duplicateInputName !== undefined
          ? value.sourceInfo.duplicateInputName
          : dragNewPane !== undefined
          ? PaneTypeNames[dragNewPane.paneType]
          : null}
      </div>
    </div>
  );
});

const keyboardShortcutClassName = "font-medium font-mono-natto text-neutral-0";
const KeyboardTransformPaneNotice = observer(() => {
  if (computed(() => keyboardTransformPaneIdBox.get() === undefined).get()) {
    return null;
  }
  return (
    <div className="absolute right-4 top-16 bg-neutral-500 text-neutral-100 p-4 rounded">
      <table>
        <tbody>
          <tr>
            <td className="pr-8">move pane</td>
            <td>
              <span className="font-medium text-neutral-0 mr-2">←↑→↓</span>
              <span className={keyboardShortcutClassName}>hjkl</span>
            </td>
          </tr>
          <tr>
            <td>move faster</td>
            <td>
              hold <span className={keyboardShortcutClassName}>shift</span>
            </td>
          </tr>
          <tr>
            <td>resize width</td>
            <td>
              <span className={keyboardShortcutClassName}>[ ]</span>
            </td>
          </tr>
          <tr>
            <td>resize height</td>
            <td>
              <span className={keyboardShortcutClassName}>{"{ }"}</span>
            </td>
          </tr>
          <tr>
            <td>finish</td>
            <td>
              <span className={keyboardShortcutClassName}>escape</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
});

function useKeyHandler() {
  useEffect(() => {
    function shouldSkipHandlingEvent(e: Event) {
      if (
        e.target instanceof HTMLElement &&
        (e.target.tagName === "TEXTAREA" ||
          e.target.tagName === "INPUT" ||
          e.target.getAttribute("contenteditable") === "true")
      ) {
        return true;
      }
      if (hasFocusGuardOpen()) {
        return true;
      }
      return false;
    }
    function onCopy(e: Event) {
      if (shouldSkipHandlingEvent(e)) {
        return;
      }
      if (selectedPaneIdsAtom.value.length > 0) {
        const normalizedPanes = addSelectionToClipboard(canvasAtom.value.panes);
        if (normalizedPanes !== undefined) {
          (e as ClipboardEvent).clipboardData?.setData(
            "application/natto",
            JSON.stringify(normalizedPanes)
          );
        }
        e.preventDefault();
      }
    }
    function onPaste(e: Event) {
      if (shouldSkipHandlingEvent(e)) {
        return;
      }
      try {
        let json = (e as ClipboardEvent).clipboardData?.getData(
          "application/natto"
        );
        if (json === undefined || json === "") {
          json = (e as ClipboardEvent).clipboardData?.getData("text/plain");
        }
        if (json !== undefined) {
          const panes = JSON.parse(json);
          assert(
            Array.isArray(panes) &&
              panes.every((p) => typeof p.type === "number")
          );
          // TODO: validate panes
          DragClipboardPasteStartEventEmitter.emit("mousedown", {
            clipboardSelection: {
              id: CLIPBOARD_PASTE_ID,
              panes,
            },
            clientPosition: [0, 0],
          });
        }
      } catch (e) {}
    }
    window.addEventListener("copy", onCopy);
    window.addEventListener("paste", onPaste);

    const unsubscribe = setupPaneCanvasKeyboard();

    return () => {
      window.removeEventListener("copy", onCopy);
      window.removeEventListener("paste", onPaste);
      unsubscribe();
      setupZoom(undefined);
    };
  }, []);
}

function useMagicLayoutLogic(animatePaneTransition: () => void) {
  useEffect(
    () =>
      autorun(async () => {
        if (urlLayoutIdAtom.value === MAGIC_LAYOUT_ID) {
          const panes = canvasAtom.value.panes;
          const elkPaneRects = await computeElkLayout(panes, undefined);
          animatePaneTransition();
          runInAction(() => {
            magicLayoutAtom.value = {
              id: MAGIC_LAYOUT_ID,
              name: MAGIC_LAYOUT_NAME,
              panes: panes.map((pane) => ({
                id: pane.id,
                rect: elkPaneRects[pane.id],
                z: pane.z,
                ...("editorHeight" in pane
                  ? { editorHeight: pane.editorHeight }
                  : {}),
              })),
            };
          });
        }
      }),
    []
  );

  const onFocusMagicPane = useCallback(async (focusPaneId: string) => {
    const panes = canvasAtom.value.panes;
    const elkPaneRects = await computeElkLayout(panes, [
      focusPaneId,
      undefined,
    ]);
    animatePaneTransition();
    runInAction(() => {
      magicLayoutAtom.value = {
        id: MAGIC_LAYOUT_ID,
        name: MAGIC_LAYOUT_NAME,
        panes: panes.map((pane) => ({
          id: pane.id,
          rect: elkPaneRects[pane.id],
          z: pane.z,
          ...("editorHeight" in pane
            ? { editorHeight: pane.editorHeight }
            : {}),
        })),
      };
    });

    const focalRect = elkPaneRects[focusPaneId];
    const focalCenter: Position = [
      focalRect[0] + focalRect[2] / 2,
      focalRect[1] + focalRect[3] / 2,
    ];
    const canvasOffset: Position = [
      focalCenter[0] - window.innerWidth / 2 / canvasViewport.zoom,
      Math.min(
        focalCenter[1] -
          (window.innerHeight - headerHeightComputed.get()) /
            3 /
            canvasViewport.zoom,
        focalRect[1] - 16
      ),
    ];
    runInAction(() => {
      canvasViewport.offset = canvasOffset;
    });
  }, []);
  const lastFocusedPaneIdRef = useRef<string | undefined>(undefined);
  useEffect(
    () =>
      reaction(
        () => focusedPaneIdAtom.value,
        (focusedPaneId) => {
          if (
            canvasLayoutIdAtom.value === MAGIC_LAYOUT_ID &&
            focusedPaneId !== undefined &&
            focusedPaneId !== lastFocusedPaneIdRef.current
          ) {
            onFocusMagicPane(focusedPaneId);
            lastFocusedPaneIdRef.current = focusedPaneId;
          }
        }
      ),
    []
  );
}

function onRootScroll(rootRef: React.MutableRefObject<HTMLDivElement | null>) {
  runInAction(() => {
    const rootElement = rootRef.current!;
    // Browser will scroll this element when focus moves offscreen.
    // Move the scroll to our controlled canvasOffset
    if (rootElement.scrollLeft !== 0) {
      canvasViewport.offset = [
        canvasViewport.offset[0] + rootElement.scrollLeft / canvasViewport.zoom,
        canvasViewport.offset[1],
      ];
      rootElement.scrollLeft = 0;
    }
    if (rootElement.scrollTop !== 0) {
      canvasViewport.offset = [
        canvasViewport.offset[0],
        canvasViewport.offset[1] + rootElement.scrollTop / canvasViewport.zoom,
      ];
      rootElement.scrollTop = 0;
    }
  });
}

export const PaneCanvas = observer(
  ({
    canvasId,
    tutorialId,
    isSynced,
  }: {
    canvasId: string;
    tutorialId?: string;
    isSynced: boolean;
  }) => {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const isInitialRenderRef = useRef(true);

    const paneIds = computed(() => canvasAtom.value.panes.map((p) => p.id), {
      equals: comparer.structural,
    }).get();

    const gridRef = useRef<HTMLDivElement | null>(null);
    const canvasElementRef = useRef<HTMLDivElement | null>(null);
    const radixPortalContainerRef = useRef<HTMLDivElement | null>(null);
    const [newPaneMenuPosition, setNewPaneMenuPosition] = useState<
      Position | undefined
    >(undefined);

    useEffect(
      action(() => {
        const focusedPaneId = focusedPaneIdAtom.value;
        if (focusedPaneId !== undefined && !paneIds?.includes(focusedPaneId)) {
          focusedPaneIdAtom.value = undefined;
        }
      }),
      [paneIds]
    );

    useEffect(() => {
      window.getSelection()?.removeAllRanges();
    }, []);

    const canvasTransitionTimeoutRef = useRef<number | undefined>(undefined);
    const animatePaneTransition = useCallback(() => {
      const canvasElement = canvasElementRef.current;
      if (canvasElement !== null) {
        canvasElement.classList.add("transition-panes");
        setTimeout(() => {
          canvasElement.classList.remove("transition-panes");
        }, 200);
      }
    }, []);
    useEffect(
      () =>
        reaction(
          () =>
            sandboxLayoutOverrideBox.get() !== undefined
              ? "layout-override"
              : canvasLayoutIdAtom.value,
          action((layoutId) => {
            setSelectedToolToPointer();
            animatePaneTransition();
            setNewPaneMenuPosition(undefined);
            focusedPaneIdAtom.value = undefined;
            selectedPaneIdsAtom.value = [];
          })
        ),
      []
    );
    useMagicLayoutLogic(animatePaneTransition);

    useLayoutEffect(() => {
      const focusPaneId = getFocusPaneIdOnMount();
      // scroll focusPane into viewport
      if (focusPaneId !== undefined) {
        const paneRect = activePaneStates.get(focusPaneId)!.rect.get();
        scrollPaneIntoViewport(paneRect);
      }
    }, [paneIds]);

    const hasMaximizedPaneComputed = computed(
      () => uiStateMobx.maximizedPaneId !== undefined
    );
    const hasMaximizedPane = hasMaximizedPaneComputed.get();
    useLayoutEffect(() => {
      return autorun(() => {
        if (hasMaximizedPaneComputed.get()) {
          canvasElementRef.current!.style.transform = `translate(${
            canvasViewport.offset[0]
          }px, ${canvasViewport.offset[1]}px) scale(${1})`;
        } else {
          const canvasViewportShouldAnimateValue = untracked(
            () => canvasViewportShouldAnimateAtom.value
          );
          if (canvasViewportShouldAnimateValue !== false) {
            canvasElementRef.current!.style.transitionProperty = "transform";
            let animationDuration = 150;
            if (typeof canvasViewportShouldAnimateValue === "number") {
              animationDuration = canvasViewportShouldAnimateValue;
              canvasElementRef.current!.style.transitionDuration = `${canvasViewportShouldAnimateValue}ms`;
            }
            if (canvasTransitionTimeoutRef.current !== undefined) {
              clearTimeout(canvasTransitionTimeoutRef.current);
            }
            canvasTransitionTimeoutRef.current = setTimeout(() => {
              canvasElementRef.current!.style.transitionProperty = "";
              canvasElementRef.current!.style.transitionDuration = "";
              canvasTransitionTimeoutRef.current = undefined;
              runInAction(() => {
                canvasViewportShouldAnimateAtom.value = false;
              });
            }, animationDuration);
          }
          const canvasTransform = canvasTransformComputed.get();
          canvasElementRef.current!.style.transform = `translate(${canvasTransform[0]}px, ${canvasTransform[1]}px) scale(${canvasViewport.zoom})`;
          canvasElementRef.current!.style.setProperty(
            "--natto-canvas-zoom",
            `${canvasViewport.zoom}`
          );
          gridRef.current!.style.backgroundSize = `${
            16 * canvasViewport.zoom
          }px`;
          gridRef.current!.style.backgroundPositionX = `${canvasTransform[0]}px`;
          gridRef.current!.style.backgroundPositionY = `${canvasTransform[1]}px`;
        }
      });
    }, []);

    useKeyHandler();
    useEffect(() => {
      isInitialRenderRef.current = false;
    }, []);

    const bindPinch = usePinch(
      action<UserHandlers["onPinch"]>(
        ({ first, origin, offset, touches, cancel, memo = {} }) => {
          if (touches < 2) {
            cancel();
            return;
          }
          if (first) {
            memo.startCanvasOffset = [...canvasViewport.offset];
            memo.prevZoom = offset[0];
            memo.lastOrigin = [...origin];
          } else {
            const newZoom = Math.round(offset[0] * 100) / 100;
            canvasViewport.offset = addPositions(
              calculateNewZoomCanvasOffset(
                canvasViewport.offset,
                origin,
                memo.prevZoom,
                newZoom
              ),
              scalePosition(
                [
                  memo.lastOrigin[0] - origin[0],
                  memo.lastOrigin[1] - origin[1],
                ],
                1 / newZoom
              )
            );
            canvasViewport.zoom = newZoom;
            memo.prevZoom = newZoom;
            memo.lastOrigin = [...origin];
          }
          return memo;
        }
      ),
      {
        from: () => [untracked(() => canvasViewport.zoom), 0],
      }
    );

    return (
      <>
        <div
          {...bindPinch()}
          className="w-screen h-viewport relative overflow-hidden"
          onScroll={() => {
            onRootScroll(rootRef);
          }}
          onMouseMove={
            isSynced
              ? (e) => {
                  canvasDispatch([
                    ActionType.PresenceCursor,
                    convertViewportPositionToCanvasOffset(
                      [e.clientX, e.clientY],
                      canvasTransformComputed.get(),
                      canvasViewport.zoom
                    ),
                  ]);
                }
              : undefined
          }
          onFocus={() => onRootScroll(rootRef)}
          ref={rootRef}
        >
          <div
            className="bg-canvas-bg bg-grid absolute top-0 bottom-0 left-0 right-0"
            ref={gridRef}
          />
          <CanvasBackground setNewPaneMenuPosition={setNewPaneMenuPosition} />
          {hasMaximizedPane ? (
            <div
              onClick={action(() => {
                uiStateMobx.maximizedPaneId = undefined;
              })}
              className="fixed top-0 left-0 right-0 bottom-0 bg-neutral-300 dark:bg-neutral-200 bg-opacity-90 backdrop-filter backdrop-blur-sm"
            />
          ) : null}
          <div
            className="absolute panesContainer transition-canvas"
            ref={canvasElementRef}
          >
            {!hasMaximizedPane ? (
              <>
                <div className="absolute top-0 -left-0.5 w-[5px] h-px bg-neutral-400" />
                <div className="absolute left-0 -top-0.5 h-[5px] w-px bg-neutral-400" />
              </>
            ) : null}
            <div
              className="absolute top-0 left-0 select-text"
              id={CANVAS_PORTAL_ID}
            ></div>
            {paneIds.map((paneId) => (
              <PaneComponent
                paneId={paneId}
                hasMaximizedPane={hasMaximizedPane}
                radixPortalContainerRef={radixPortalContainerRef}
                key={paneId}
              />
            ))}
            <DragPaneOutputWire />
            {computed(() => canvasLayoutIdAtom.value === undefined).get() ? (
              <DragNewPanePreview />
            ) : null}
            {!hasMaximizedPane ? <PresenceCursors /> : null}
          </div>
        </div>
        <DragClipboardPastePreview />
        <DragEvalPaneTemplatePreview />
        {newPaneMenuPosition !== undefined ? (
          <PopperLayer
            reference={{
              current: {
                getBoundingClientRect: (): any => {
                  const canvasTransform = canvasTransformComputed.get();
                  const x =
                    newPaneMenuPosition[0] * canvasViewport.zoom +
                    canvasTransform[0];
                  const y =
                    newPaneMenuPosition[1] * canvasViewport.zoom +
                    canvasTransform[1];
                  return {
                    width: 0,
                    height: 0,
                    top: y,
                    bottom: y,
                    left: x,
                    right: x,
                  };
                },
              },
            }}
            render={({ state, completeTransitionExit }) => (
              <AnimatedPopout
                state={state}
                completeTransitionExit={completeTransitionExit}
                transformOrigin="top left"
              >
                <NewPaneContextMenu
                  position={newPaneMenuPosition}
                  onClose={() => setNewPaneMenuPosition(undefined)}
                />
              </AnimatedPopout>
            )}
            options={{ placement: "bottom-start" }}
            onOutsideMouseDown={() => setNewPaneMenuPosition(undefined)}
            transitionExit={true}
          />
        ) : null}
        <div className="absolute top-0 left-0" ref={radixPortalContainerRef} />
        {isEditingLayoutAtom.value ? (
          <LayoutEditPane />
        ) : canvasLayoutIdAtom.value !== undefined &&
          canvasLayoutIdAtom.value !== MAGIC_LAYOUT_ID &&
          (isSynced || canvasSelectionAtom.value?.[0] !== "user") ? (
          <button
            onClick={action(() => {
              isEditingLayoutAtom.value = true;
            })}
            className="absolute top-24 md:top-16 right-4 button"
          >
            edit layout
          </button>
        ) : null}
        {computed(() => sandboxLayoutOverrideBox.get() !== undefined).get() ? (
          <button
            onClick={action(() => {
              sandboxLayoutOverrideBox.set(undefined);
            })}
            className="button absolute top-24 md:top-16 right-4 group"
          >
            exit sandbox layout{" "}
            <span className="text-neutral-400 ml-1 group-hover:text-neutral-200 transition">
              ⌃⌘L
            </span>
          </button>
        ) : null}
        <MinimapPane />
        {paneIds === undefined ? (
          <div className="absolute top-14 right-4 text-neutral-400 z-99999">
            loading...
          </div>
        ) : null}
        {tutorialId !== undefined ? (
          <TutorialPane tutorialId={tutorialId} />
        ) : null}
        <KeyboardTransformPaneNotice />
        <div
          className={classNames(
            "fixed left-0 top-0 right-0 h-20 md:h-12 bg-neutral-0 dark:bg-neutral-100 flex items-end",
            TOPBAR_CLASSNAME,
            { hidden: hasMaximizedPane }
          )}
        >
          <div className="absolute top-0 left-0 right-0 bg-secondary-50 h-10 border-b border-secondary-200 md:hidden" />
          <div className="h-10 md:h-12 flex w-full">
            <ToolbarPointerButton className="md:ml-36" />
            <ToolbarPanButton />
            <ToolbarAddPaneButton />
            <div className="grow flex justify-center">
              {isSynced ? (
                <div className="absolute top-1.5 left-32 md:static flex items-center">
                  <CanvasNameUnitSandbox canvasId={canvasId} />
                </div>
              ) : null}
            </div>
            <div className="flex relative">
              {isSynced ? (
                <div className="relative h-full flex">
                  <HeaderPresenceButton canvasId={canvasId} />
                </div>
              ) : usedDefaultLayoutAtom.value ? (
                <div className="hidden lg:flex h-full mx-1 items-center">
                  <button
                    onClick={() => {
                      ParentComlinkAPI.switchLayoutId(undefined);
                    }}
                    className="px-2 py-1 bg-primary-100 text-neutral-500 hover:bg-primary-500 hover:text-neutral-0 rounded transition"
                  >
                    view source
                  </button>
                </div>
              ) : null}
              <HeaderLayoutButton />
              <ToolbarZoomButton />
            </div>
          </div>
        </div>
        <div className="absolute top-0 left-0 right-0 z-99999">
          <LayerContainer />
        </div>
      </>
    );
  }
);
